import { useCallback, useMemo } from 'react'
import type { JSX, ReactNode } from 'react'

import { clsx } from 'clsx'
import type { NodeEntry, Value } from 'platejs'
import type {
  PlateContentProps,
  PlateEditor,
  TPlateEditor,
} from 'platejs/react'
import type { BaseRange } from 'slate'

import { RichTextField } from '@buffer-mono/popcorn/rich-text'

import { isSelectionChangeOnly } from './isSelectionChangeOnly'

import styles from './EditorSurface.module.css'

export interface EditorSurfaceProps<V extends Value> {
  editor: TPlateEditor<V>
  editableProps: Omit<
    PlateContentProps,
    'translate' | 'decorate' | 'readOnly'
  > & {
    decorate?: (options: {
      editor: PlateEditor
      entry: NodeEntry
    }) => BaseRange[]
    readOnly?: boolean
  }
  onChange: (value: V) => void
  children?: ReactNode
}

export const EditorSurface = <V extends Value>({
  editor,
  editableProps,
  onChange,
  children,
}: EditorSurfaceProps<V>): JSX.Element => {
  const { readOnly, ...restEditableProps } = editableProps

  const stableEditableProps = useMemo<
    typeof restEditableProps & { translate: 'no' }
  >(
    () => ({
      ...restEditableProps,
      className: clsx(styles.editable, restEditableProps.className),
      // Guard the contenteditable from browser translation: Google Translate
      // rewrites text nodes inside Slate's managed DOM (wrapping them in <font>),
      // which both corrupts the user's draft and crashes React on reconciliation.
      translate: 'no',
    }),
    [restEditableProps],
  )

  const handleChange = useCallback(
    ({ value }: { editor: TPlateEditor<V>; value: V }): void => {
      if (isSelectionChangeOnly(editor)) {
        return
      }

      onChange(value)
    },
    [editor, onChange],
  )

  return (
    <RichTextField.Root
      editor={editor}
      onChange={handleChange}
      readOnly={readOnly}
      variant="embedded"
      className={styles.root}
      // Every mounted <Plate> instance increments a shared
      // `globalThis.__PLATE_INSTANCES__` counter, so a session with Composer
      // and another Plate-based surface (e.g. the Changelog panel) mounted at
      // once trips Plate's "multiple instances" warning. False positive here
      // — the two never share an editor or document — so silence it rather
      // than chase mount timing that can't actually prevent it.
      suppressInstanceWarning
    >
      <RichTextField.Editable {...stableEditableProps} />
      {children}
    </RichTextField.Root>
  )
}
